import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser } from "@/lib/sentry";
import i18n from "@/i18n";

/** Attach id/email/role to Sentry events (no tokens, no PII beyond email). */
async function syncSentryUser(session: Session | null) {
  if (!session?.user) {
    setSentryUser(null);
    return;
  }
  setSentryUser({ id: session.user.id, email: session.user.email });
  try {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id);
    const role = (data ?? []).map((r: { role: string }) => r.role).sort().join(",") || "user";
    setSentryUser({ id: session.user.id, email: session.user.email }, role);
  } catch {
    /* non-fatal */
  }
}

function currentLocale(): "en" | "es" | "pt" {
  const lng = (i18n.language || "en").slice(0, 2).toLowerCase();
  return (["en", "es", "pt"].includes(lng) ? lng : "en") as "en" | "es" | "pt";
}

async function ensurePreferredLanguage(session: Session | null) {
  if (!session?.user) return;
  const stored = (session.user.user_metadata as Record<string, unknown> | undefined)?.preferred_language;
  const lng = currentLocale();
  if (stored === lng) return;
  try {
    await supabase.auth.updateUser({ data: { preferred_language: lng } });
  } catch {
    // non-fatal
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sendWelcomeIfNew = async (session: Session | null) => {
      if (!session?.user?.email) return;
      const key = `welcome_email_sent_${session.user.id}`;
      if (localStorage.getItem(key)) return;
      // Only send if account was just created (within last 5 min)
      const created = session.user.created_at ? new Date(session.user.created_at).getTime() : 0;
      if (!created || Date.now() - created > 5 * 60 * 1000) {
        localStorage.setItem(key, "1");
        return;
      }
      localStorage.setItem(key, "1");
      // Persist preferred language BEFORE sending welcome so the email uses it
      await ensurePreferredLanguage(session);
      try {
        await supabase.functions.invoke("send-welcome-email", {
          body: {
            templateData: {
              locale: currentLocale(),
              name:
                session.user.user_metadata?.full_name ||
                session.user.user_metadata?.name ||
                session.user.email?.split("@")[0],
            },
          },
        });
      } catch (err) {
        console.error("Welcome email failed", err);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        void syncSentryUser(session);
        if (event === "SIGNED_IN") {
          // Defer so i18n geo detection (which may still be in-flight) has a chance to land
          setTimeout(() => {
            void ensurePreferredLanguage(session);
            void sendWelcomeIfNew(session);
          }, 1500);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      void syncSentryUser(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
